<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="sales"
        :row-key="'sale_id'"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1180 }"
        class="text-nowrap align-middle sales-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">
            {{ $t(`${i18nScope}.no_sales_reports_found`) }}
          </div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at) }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="`status-${String(record.status || 'pending').toLowerCase()}`">
              {{ formatLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'invoice_total'">
            ${{ formatAmount(record.invoice_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            ${{ formatAmount(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            ${{ formatAmount(getDue(record)) }}
          </template>

          <template v-else-if="column && column.key === 'payment_status'">
            <span class="status-badge" :class="`payment-${String(record.payment_status || 'pending').toLowerCase()}`">
              {{ formatLabel(record.payment_status) }}
            </span>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { SalesReportItem } from "@/services/report";

export default defineComponent({
  name: "SalesReport",
  props: {
    sales: {
      type: Array as () => SalesReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    i18nScope: {
      type: String,
      default: "sales_report_page",
    },
  },
  emits: ["table-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_date`), key: "date", dataIndex: "created_at", width: 210, align: "center" as const },
      { title: t(`${props.i18nScope}.table_reference`), key: "reference_no", dataIndex: "reference_no", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_customer`), key: "customer_name", dataIndex: "customer_name", width: 220, align: "center" as const },
      { title: t(`${props.i18nScope}.table_status`), key: "status", dataIndex: "status", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_grand_total`), key: "invoice_total", dataIndex: "invoice_total", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_paid`), key: "paid", dataIndex: "paid", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_due`), key: "due", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_payment_status`), key: "payment_status", dataIndex: "payment_status", width: 170, align: "center" as const },
    ]);

    const formatDate = (dateString?: string) => {
      if (!dateString) return t(`${props.i18nScope}.not_available`);
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatAmount = (value?: string | number) => Number(value ?? 0).toFixed(2);
    const getDue = (record: SalesReportItem) =>
      Number(record.invoice_total ?? 0) - Number(record.paid ?? 0);
    const formatLabel = (value?: string) => {
      const text = String(value || "pending").toLowerCase();
      return t(`${props.i18nScope}.status_${text}`, text.charAt(0).toUpperCase() + text.slice(1));
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit("table-change", pagination.current || 1, pagination.pageSize || 10);
    };

    return {
      columns,
      formatDate,
      formatAmount,
      getDue,
      formatLabel,
      handleChange,
    };
  },
});
</script>

<style scoped>
.sales-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.sales-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-received,
.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-ordered,
.status-pending {
  color: #d48a00;
  background: #fff4db;
}

.status-cancelled {
  color: #d33c43;
  background: #fdecec;
}

.payment-paid {
  color: #1f9254;
  background: #e8f7ee;
}

.payment-partial {
  color: #d48a00;
  background: #fff4db;
}

.payment-pending,
.payment-unpaid,
.payment-due {
  color: #d33c43;
  background: #fdecec;
}
</style>
